import { REWARD_AMOUNT_TYPE } from 'components/earning/RewardAmountInputArea';
import { REWARD_LOGIC_TYPE } from 'components/earning/RewardLogicSelector';
import {
  APIStatus,
  CheckStatus,
  DISPLAY_TARGET_CUSTOMER_TYPE,
  EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE,
  EarningRuleLinkedCampaign,
  EarningRuleRewardType,
  EarningRuleType,
  EarningRuleTypeKey,
  LanguageConfig,
  SESSION_KEYS,
  TARGET_CUSTOMER_TYPE,
} from 'config/CustomEnums';
import { apiWithResponseHandle } from 'models/LoadingUtil';
import { defaultStep, getNewStepConfig } from 'models/StepBarUtil';
import {
  createOrUpdateEarningRule,
  createOrUpdateWebhookAuthenticatedKey,
  getEarningRuleProducts,
  getOneEarningRule,
  getWebhookAuthenticatedKey,
} from 'services/EarningRuleHelper';

import {
  convertNumberToCursor,
  convertPKToId,
  getDisplayLastModifiedBy,
  getObjectFromSessionStorage,
  removeFromSessionStorage,
  saveToSessionStorage,
} from 'utils';
import { returnDefaultValueWhileEmpty } from 'utils/StringUtil';
import { getDetailPageDisplayLastModifiedDate, getReadablePeriod } from 'utils/TimeFormatUtil';
import {
  getDisplayRecurringPeriod,
} from 'models/CreateCampaignModel';

export const EARNING_RULES_SESSION_KEY = SESSION_KEYS.EARNING_RULES_SESSION_KEY;

const stepNames = ['Type', 'Content', 'Properties'];

const getInitialState = () => ({
  currentStep: 0,
  stepConfig: defaultStep(stepNames),
  earningRule: {
    specialSkus: [],
    inputSkus: [],
    targetCustomerType: TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT
  },
  skusHaveLoaded: false,
  errorFields: [],
  formChanged: false,
  checked: CheckStatus.initOrNotChecked,
  hasUpdatedDefaultValues: false,
  createStatus: APIStatus.none,
  webhookAuthenticatedKey: {},
  isCopied: false,
});

const postTranslations = (data) => {
  if (!data) {
    return [];
  }
  const languageList = Object.keys(data).filter(
    (language) => language !== LanguageConfig.english,
  );
  const translations = languageList.map((language) => {
    const newData = {
      language,
      name: data?.[language]?.generalName,
      instructionSectionTitle: data?.[language]?.instructionSectionTitle,
      instructionSectionContent: data?.[language]?.instructionSectionContent,
      detailSectionTitle: data?.[language]?.detailSectionTitle,
      detailSectionContent: data?.[language]?.detailSectionContent,
    };
    const id = data?.[language]?.pk;
    if (id) {
      newData.id = id;
    }
    return newData;
  });
  return translations;
};

const getTranslations = (data) => {
  const translationList = data.translations?.edges || [];
  const translation = {};
  translationList.forEach((item) => {
    const language = item.node.language;
    translation[language] = {
      ...item.node,
      generalName: item.node.name,
      // name: item.node.name,
    };
  });
  translation[LanguageConfig.english] = {
    generalName: data.name,
    instructionSectionTitle: data.instructionSectionTitle,
    instructionSectionContent: data.instructionSectionContent,
    detailSectionTitle: data.detailSectionTitle,
    detailSectionContent: data.detailSectionContent,
    pk: data.pk,
  };
  return translation;
};

const getQuantity = (type, rewardType, data) => {
  let quantity = null;
  switch (rewardType) {
    case EarningRuleRewardType.points:
      quantity = data[`${type}TypePointsRewardTypePoints`];
      break;
    case EarningRuleRewardType.stamps:
      quantity = data[`${type}TypeStampRewardTypeQuantity`];
      break;
    default:
      quantity = data[`${type}TypeCouponRewardTypeQuantity`];
  }
  return quantity;
};

const getListStrings = (list) => {
  if (list?.length === 0) {
    return null;
  }
  return list
    ?.map((item) => item.name || item?.node.name || item?.node.levelName)
    .toString();
};

function getMillisecondsFromDate(date) {
  if (!date) {
    return '';
  }
  const momentTime = new Date(date);
  return momentTime;
}

const parseLineItemGroups = (lineItemGroups) => {
  if (!lineItemGroups) {
    return [];
  };
  return lineItemGroups.map(group => {
    const groupRequirements = [];
    [
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.brand,
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.category,
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.subcategory
    ].forEach(fieldType => {
      if (group[fieldType]?.pk) {
        groupRequirements.push({
          data: group[fieldType],
          fieldType,
        });
      };
    });

    [
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.transactionAdditionalData,
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.purchasedItemAdditionalData
    ].forEach(fieldType => {
      if (group[fieldType]) {
        group[fieldType].forEach(data => {
          if (data.field?.pk) {
            groupRequirements.push({
              data: {
                ...data,
                field: {
                  ...data?.field,
                  name: data?.field?.fieldName
                }
              },
              fieldType,
            });
          }
        })
      }
    });

    if (group.sku) {
      groupRequirements.push({
        data: group.sku,
        fieldType: EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.sku
      })
    }

    return {
      pk: group.pk,
      minimumSpending: group.minimumSpending,
      minimumQuantity: group.minimumQuantity,
      requirements: groupRequirements,
    }
  })
}

const getSelectedStores = (stores) => {
  if(!stores) {
    return []
  }
  return stores.map((item)=> {
    const visibleInFrontEnd = item[2] == "true"
    const name = item[1]
    return {
      id: parseInt(item[0]),
      pk: parseInt(item[0]),
      name,
      visibleInFrontEnd,
      displayStoreName: `${name}${visibleInFrontEnd ? '' : '(closed)'}`
    }
  })
}

const parseEarningRule = (defaultData) => {
  if (!defaultData) {
    return {};
  }
  let formatData = defaultData;
  let commonData = {};

  let targetCustomerType = defaultData?.targetType
  const targetedSegments = defaultData.targetedSegments?.edges?.map(
    (item) => item.node,
  );
  const blacklistSegments = defaultData?.targetedBlacklistSegments?.edges?.map(
    (item) => item.node,
  );
  let dynamicSegments = [];
  let staticSegments = []
  let targetedLevels = []
  if (targetCustomerType === TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT) {
    dynamicSegments = targetedSegments
  } else if (targetCustomerType === TARGET_CUSTOMER_TYPE.STATIC_SEGMENT) {
    staticSegments = targetedSegments
  } else if (targetCustomerType === TARGET_CUSTOMER_TYPE.LEVEL) {
    targetedLevels = defaultData.targetedLevels?.edges?.map((item) => {
      const { node } = item;
      return {
        ...node,
        name: node.levelName,
      };
    });
  }
  if (Object.keys(defaultData).length > 0) {
    const type = EarningRuleTypeKey[defaultData.type];
    formatData = {
      id: defaultData.id,
      pk: defaultData.pk,
      lindedCampaigns: defaultData.campaigns?.edges?.map(item => item?.node),
      lindedStampCampaigns: defaultData.stampTaskRelation?.edges?.map(item => item?.node?.stampCampaign),
      type: defaultData.type,
      generalName: defaultData.name,
      minSpending: defaultData.generalPurchaseTypeMinimumSpending === 0 ? "0" : defaultData.generalPurchaseTypeMinimumSpending,
      minQuantity: defaultData.generalPurchaseTypeMinimumQuantity === 0 ? "0" : defaultData.generalPurchaseTypeMinimumQuantity,
      maxSpending: defaultData.generalPurchaseTypeMaximumSpending === 0 ? "0" : defaultData.generalPurchaseTypeMaximumSpending,
      eligibleDays: defaultData.generalPurchaseTypeEligibleNumberOfDaysSincePurchase === 0
        ? "0"
        : defaultData.generalPurchaseTypeEligibleNumberOfDaysSincePurchase,
      uploadEligibleDays: defaultData.generalPurchaseTypeUploadTransactionEligibleDays === 0
        ? "0"
        : defaultData.generalPurchaseTypeUploadTransactionEligibleDays,
      selectedStores: getSelectedStores(defaultData?.linkedPureStores),
      isExclusive: defaultData.isExclusive,
      displayIsExclusive: defaultData.isExclusive ? 'Yes' : 'No',
      isExcludeDeliveryCost:
        defaultData.generalPurchaseTypeWillExcludeDeliveryCost,
      isExcludeOtherCharges:
        defaultData.generalPurchaseTypeWillExcludeOtherCharges,
      isInBirthMonth: defaultData.generalPurchaseTypeOnlyForBirthMonth,
      displayIsInBirthMonth: defaultData.generalPurchaseTypeOnlyForBirthMonth
        ? 'Yes'
        : 'No',
      specialSkus: [],
      inputSkus: [],

      referralType: defaultData.memberReferralTypeBeneficiary,
      maxInvitees: defaultData.memberReferralTypeLimit,

      latitude: defaultData.gpsCheckInTypeLatitude,
      longitude: defaultData.gpsCheckInTypeLongitude,
      radius: defaultData.gpsCheckInTypeRadiusInMeter,

      qrCodes: defaultData.qrCodeScanningTypeQrCodes,

      fillingForm: defaultData.fillingFormTypeForm,
      definitionType: defaultData.fillingFormTypeDefinitionType,

      levelGoal: defaultData.levelUpgradeTypeLevelGoal,

      rewardType: defaultData[`${type}TypeRewardType`],
      quantity: getQuantity(
        type,
        defaultData[`${type}TypeRewardType`],
        defaultData,
      ),
      tpeQuantity:
        defaultData[`${type}TypeRewardType`] === EarningRuleRewardType.points
          ? defaultData[`${type}TypePointsRewardTypeTpe`]
          : null,
      coupons: defaultData[`${type}TypeCouponRewardTypeCouponTemplate`],
      translations: getTranslations(defaultData),
      linkedStampCampaign:
        defaultData.linkedCampaignType === 'STAMP_CAMPAIGN' ? true : false,
      displayLinkedStampCampaign:
        defaultData.linkedCampaignType === 'STAMP_CAMPAIGN' ? 'Yes' : 'No',
      targetCustomerType,
      displayTargetCustomerType: defaultData.isExclusive
        ? DISPLAY_TARGET_CUSTOMER_TYPE?.[targetCustomerType]
        || targetCustomerType
        : null,
      targetedSegments,
      dynamicSegments,
      staticSegments,
      targetedLevels,
      excludeBlacklistSegment: defaultData?.excludeBlacklistSegment,
      blacklistSegments,
      displayExcludeBlacklistSegment: defaultData?.excludeBlacklistSegment ? 'Yes' : 'No',
      displayBlacklistSegments: defaultData?.excludeBlacklistSegment
        ? getListStrings(
          blacklistSegments || [],
        ) : null,
      displayDynamicSegments: getListStrings(
        dynamicSegments || [],
      ),
      displayStaticSegments: getListStrings(
        staticSegments || [],
      ),
      displayTargetedLevels: getListStrings(
        defaultData?.targetedLevels?.edges || [],
      ),
      displayActivePeriod: getReadablePeriod(
        defaultData.startDate,
        defaultData.endDate,
      ),
      activeStartDate: getMillisecondsFromDate(defaultData.startDate),
      activeEndDate:
        getMillisecondsFromDate(defaultData.endDate) ||
        getMillisecondsFromDate(defaultData.startDate),
      activePeriodValues: defaultData.blackoutPeriod?.edges?.map((item) => {
        return {
          startDate: item.node.startDate,
          endDate: item.node.endDate,
          id: item.node.pk,
        };
      }),
      activeWeekday: defaultData.blackoutWeekday,
      isAlwaysActivePeriod: !defaultData.endDate,
      creationDate: defaultData.creationDate,
      displayCreationDate: getDetailPageDisplayLastModifiedDate(defaultData?.creationDate),
      displayLastModifiedDate: getDetailPageDisplayLastModifiedDate(defaultData?.lastModifiedDate),
      displayLastModifiedBy: getDisplayLastModifiedBy(defaultData),
      executionPriority: defaultData?.triggerPriority,
      recurringDate: defaultData?.recurringDate,
      displayRecurringActivePeriod: getDisplayRecurringPeriod({
        isForBirth: defaultData.generalPurchaseTypeOnlyForBirthMonth || defaultData.type == EarningRuleType.birthday,
        recurringDate: defaultData?.recurringDate,
        prefix: "Active"
      })
    };

    switch (defaultData.type) {
      case EarningRuleType.generalPurchase:
        const parsedGeneralPurchaseTypeOverallRewardLimit = JSON.parse(
          defaultData.generalPurchaseTypeOverallRewardLimit || "{}"
        )
        const parsedGeneralPurchaseTypePeriodicRewardLimit = JSON.parse(
          defaultData.generalPurchaseTypePeriodicRewardLimit || "{}"
        )
        const parsedGeneralPurchaseTypePerHeadRewardLimit = JSON.parse(
          defaultData.generalPurchaseTypePerHeadRewardLimit || "{}"
        )
        const parsedGeneralPurchaseTypePerHeadPeriodicRewardLimit = JSON.parse(
          defaultData.generalPurchaseTypePerHeadPeriodicRewardLimit || "{}"
        )
        const parsedGeneralPurchaseTypePerTransactionRewardLimit = JSON.parse(
          defaultData.generalPurchaseTypePerTransactionRewardLimit || "{}"
        )
        const getRewardValue = (value) => value === 0 ? "0" : value
        commonData = {
          lineItemRequirement: defaultData.generalPurchaseTypeLineItemRequirement || false,
          useAdvancedLineItemGroups: defaultData.generalPurchaseTypeUseAdvancedLineItemGroups || false,
          lineITemGroupsUseOr: defaultData.generalPurchaseTypeLineItemGroupsUseOrRelationship || false,
          lineItemGroups: parseLineItemGroups(defaultData.generalPurchaseTypeLineItemGroups),
          rewardLogicType: defaultData.generalPurchaseTypeRewardLogicType,
          rewardAmountType: defaultData.generalPurchaseTypeRewardAmountLogicType,
          rewardAmountBasedType: defaultData.generalPurchaseTypeRewardAmountBasedType,
          skuFileImportTime: defaultData.generalPurchaseTypeLineItemRequirement ? defaultData.generalPurchaseTypeSkuFileImportTime : null,
          couponTypeRewardPerX: getRewardValue(defaultData.generalPurchaseTypeCouponRewardTypeCouponX),
          couponTypeRewardPerXValue: getRewardValue(defaultData.generalPurchaseTypeCouponRewardTypeCouponPerXValue),
          stampTypeRewardPerX: getRewardValue(defaultData.generalPurchaseTypeStampRewardTypeX),
          stampTypeRewardPerXValue: getRewardValue(defaultData.generalPurchaseTypeStampRewardTypeStampsPerXValue),
          pointTypeRewardPerXValue: getRewardValue(defaultData.generalPurchaseTypePointsRewardTypePointsPerXValue),
          pointTypeRewardPerX: getRewardValue(defaultData.generalPurchaseTypePointsRewardTypeX),
          tpeTypeRewardPerXValue: getRewardValue(defaultData.generalPurchaseTypePointsRewardTypeTpePerXValue),
          tpeTypeRewardPerX: getRewardValue(defaultData.generalPurchaseTypePointsRewardTypeTpeX),
          // quantity:
          //   defaultData.generalPurchaseTypeRewardType ===
          //     EarningRuleRewardType.points
          //     ? defaultData.generalPurchaseTypePointsRewardTypePointsPerXDollarsSpent
          //     : defaultData.generalPurchaseTypeRewardType ===
          //       EarningRuleRewardType.stamps
          //       ? defaultData.generalPurchaseTypeStampRewardTypeStampsPerXDollarsSpent
          //       : defaultData.generalPurchaseTypeCouponRewardTypeQuantity,

          // tpeQuantity:
          //   defaultData.generalPurchaseTypePointsRewardTypeTpePerXDollarsSpent,
          // rewardTypeX:
          //   defaultData.generalPurchaseTypeRewardType ===
          //     EarningRuleRewardType.points
          //     ? defaultData.generalPurchaseTypePointsRewardTypeX
          //     : defaultData.generalPurchaseTypeStampRewardTypeX,
          displayStampReward: defaultData.generalPurchaseTypeRewardType === EarningRuleRewardType.stamps
            ? `HK$ ${defaultData.generalPurchaseTypeStampRewardTypeStampsPerXDollarsSpent} = ${defaultData.generalPurchaseTypeStampRewardTypeX} stamps`
            : "-",
          // rewardTypeTpeX: defaultData.generalPurchaseTypePointsRewardTypeTpeX,
          overallLimit: defaultData[`${type}TypeOverallLimit`],
          perHeadLimit: defaultData[`${type}TypePerHeadLimit`],
          periodicLimit: defaultData[`${type}TypePeriodicLimit`],
          periodicLimitDays:
            defaultData[`${type}TypePeriodicLimitEffectiveNumberOfDays`],
          perHeadPeriodicLimit: defaultData[`${type}TypePerHeadPeriodicLimit`],
          perHeadPeriodicLimitDays:
            defaultData[`${type}TypePerHeadPeriodicLimitEffectiveNumberOfDays`],

          earningRuleAllTimeLimitCoupons: parsedGeneralPurchaseTypeOverallRewardLimit?.COUPONS,
          earningRuleAllTimeLimitPoints: parsedGeneralPurchaseTypeOverallRewardLimit?.POINTS,
          earningRuleAllTimeLimitPp: parsedGeneralPurchaseTypeOverallRewardLimit?.PROGRESSION_POINTS,
          earningRuleAllTimeLimitStamps: parsedGeneralPurchaseTypeOverallRewardLimit?.STAMPS,
          earningRuleAllTimeLimitMaxTriggerTimes: defaultData.generalPurchaseTypeOverallLimit,
          earningRuleTimePeriodLimitPeriodicType: defaultData.generalPurchaseTypePeriodicType,
          earningRuleTimePeriodLimitPeriodicTypePerDays: defaultData.generalPurchaseTypePeriodicLimitEffectiveNumberOfDays,
          earningRuleTimePeriodLimitPeriodicFollowActive: defaultData.generalPurchaseTypePeriodicFollowActivePeriod,
          earningRuleTimePeriodLimitPeriodicStartTime: defaultData.generalPurchaseTypePeriodicStartTime,
          earningRuleTimePeriodLimitPeriodicEndTime: defaultData.generalPurchaseTypePeriodicEndTime,
          earningRuleTimePeriodLimitCoupons: parsedGeneralPurchaseTypePeriodicRewardLimit?.COUPONS,
          earningRuleTimePeriodLimitPoints: parsedGeneralPurchaseTypePeriodicRewardLimit?.POINTS,
          earningRuleTimePeriodLimitPp: parsedGeneralPurchaseTypePeriodicRewardLimit?.PROGRESSION_POINTS,
          earningRuleTimePeriodLimitStamps: parsedGeneralPurchaseTypePeriodicRewardLimit?.STAMPS,
          earningRuleTimePeriodLimitMaxTriggerTimes: defaultData.generalPurchaseTypePeriodicLimit,

          perMemberAllTimeLimitCoupons: parsedGeneralPurchaseTypePerHeadRewardLimit?.COUPONS,
          perMemberAllTimeLimitPoints: parsedGeneralPurchaseTypePerHeadRewardLimit?.POINTS,
          perMemberAllTimeLimitPp: parsedGeneralPurchaseTypePerHeadRewardLimit?.PROGRESSION_POINTS,
          perMemberAllTimeLimitStamps: parsedGeneralPurchaseTypePerHeadRewardLimit?.STAMPS,
          perMemberAllTimeLimitMaxTriggerTimes: defaultData?.generalPurchaseTypePerHeadLimit,
          perMemberTimePeriodLimitPeriodicType: defaultData?.generalPurchaseTypePerHeadPeriodicType,
          perMemberTimePeriodLimitPeriodicTypePerDays: defaultData?.generalPurchaseTypePerHeadPeriodicLimitEffectiveNumberOfDays,
          perMemberTimePeriodLimitPeriodicFollowActivePeriod: defaultData?.generalPurchaseTypePerHeadPeriodicFollowActivePeriod,
          perMemberTimePeriodLimitPeriodicStartTime: defaultData?.generalPurchaseTypePerHeadPeriodicStartTime,
          perMemberTimePeriodLimitPeriodicEndTime: defaultData?.generalPurchaseTypePerHeadPeriodicEndTime,
          perMemberTimePeriodLimitCoupons: parsedGeneralPurchaseTypePerHeadPeriodicRewardLimit?.COUPONS,
          perMemberTimePeriodLimitPoints: parsedGeneralPurchaseTypePerHeadPeriodicRewardLimit?.POINTS,
          perMemberTimePeriodLimitPp: parsedGeneralPurchaseTypePerHeadPeriodicRewardLimit?.PROGRESSION_POINTS,
          perMemberTimePeriodLimitStamps: parsedGeneralPurchaseTypePerHeadPeriodicRewardLimit?.STAMPS,
          perMemberTimePeriodLimitMaxTriggerTimes: defaultData?.generalPurchaseTypePerHeadPeriodicLimit,

          perTransactionLimitCoupons: parsedGeneralPurchaseTypePerTransactionRewardLimit?.COUPONS,
          perTransactionLimitPoints: parsedGeneralPurchaseTypePerTransactionRewardLimit?.POINTS,
          perTransactionLimitPp: parsedGeneralPurchaseTypePerTransactionRewardLimit?.PROGRESSION_POINTS,
          perTransactionLimitStamps: parsedGeneralPurchaseTypePerTransactionRewardLimit?.STAMPS,
        };
        break;
      case EarningRuleType.gpsCheckIn:
      case EarningRuleType.qrCodeScanning:
        commonData = {
          overallLimit: defaultData[`${type}TypeOverallLimit`],
          perHeadLimit: defaultData[`${type}TypePerHeadLimit`],
          periodicLimit: defaultData[`${type}TypePeriodicLimit`],
          periodicLimitDays:
            defaultData[`${type}TypePeriodicLimitEffectiveNumberOfDays`],
          perHeadPeriodicLimit: defaultData[`${type}TypePerHeadPeriodicLimit`],
          perHeadPeriodicLimitDays:
            defaultData[`${type}TypePerHeadPeriodicLimitEffectiveNumberOfDays`],
          displayStampReward: defaultData[`${type}TypeRewardType`] === EarningRuleRewardType.stamps
            ? defaultData[`${type}TypeStampRewardTypeQuantity`] || '-'
            : "-",
        };
        break
      case EarningRuleType.fillingForm:
        commonData = {
          overallLimit: defaultData[`${type}TypeOverallLimit`],
          perHeadLimit: defaultData[`${type}TypePerHeadLimit`],
          periodicLimit: defaultData[`${type}TypePeriodicLimit`],
          periodicLimitDays:
            defaultData[`${type}TypePeriodicLimitEffectiveNumberOfDays`],
          perHeadPeriodicLimit: defaultData[`${type}TypePerHeadPeriodicLimit`],
          perHeadPeriodicLimitDays:
            defaultData[`${type}TypePerHeadPeriodicLimitEffectiveNumberOfDays`],
        };
        break;
      case EarningRuleType.memberReferral:
        commonData = {
          displayStampReward: defaultData.memberReferralTypeRewardType === EarningRuleRewardType.stamps
            ? defaultData.memberReferralTypeStampRewardTypeQuantity || '-'
            : "-",
        };
        break;
      case EarningRuleType.birthday:
        commonData = {};
        break;
      case EarningRuleType.levelUpgrade:
        commonData = {
          perHeadPeriodicLimit: defaultData[`${type}TypePerHeadPeriodicLimit`],
          perHeadPeriodicLimitDays:
            defaultData[`${type}TypePerHeadPeriodicLimitEffectiveNumberOfDays`],
        };
        break;
      case EarningRuleType.newMember:
        commonData = {
          requirePromotionCode: defaultData.newMemberTypeRequirePromotionCode || false,
          promotionCodeUsageMaxLimit: defaultData.newMemberTypePromotionCodeUsageMaxLimit,
          promotionCode: defaultData.newMemberTypePromotionCode,
          promotionCodeAvailableTimes: defaultData.newMemberTypePromotionCodeAvailableTimes === 0 ? "0" : defaultData.newMemberTypePromotionCodeAvailableTimes,
        };
        break;
      default:
        break;
    }
  }

  const earningRule = { ...formatData, ...commonData };
  return earningRule;
};

const cleanLineItemGroups = (groups) => {
  return groups?.map(item => {
    const groupData = {
      minimumSpending: item?.minimumSpending || null,
      minimumQuantity: item?.minimumQuantity || null,
    };
    if (item?.pk) {
      groupData["id"] = item?.pk;
    }

    const transactionAdditionalDataList = [];
    const purchasedItemAdditionalDataList = [];
    // eslint-disable-next-line no-unused-expressions
    item?.requirements?.forEach(requirement => {
      const fieldType = requirement?.fieldType;
      if ([
        EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.brand,
        EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.category,
        EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.subcategory,
      ].includes(fieldType)) {
        groupData[fieldType] = requirement?.data?.pk || null;
      };

      if ([
        EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.transactionAdditionalData,
        EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.purchasedItemAdditionalData
      ].includes(fieldType)) {
        const additionalData = {
          field: requirement?.data?.field?.pk || null,
          value: requirement?.data?.value || null,
        };
        if (requirement?.data?.pk) {
          additionalData["id"] = requirement?.data?.pk;
        };
        if (fieldType === EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.transactionAdditionalData) {
          transactionAdditionalDataList.push(additionalData);
        } else {
          purchasedItemAdditionalDataList.push(additionalData);
        }
      }

      if (fieldType === EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.sku) {
        groupData["sku"] = requirement?.data || null;
      }
    });

    // delete requirement
    [
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.brand,
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.category,
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.subcategory,
      EARNING_RULE_LINE_ITEM_GROUP_REQUIREMENT_TYPE.sku,
    ].forEach(item => {
      if (!(item in groupData)) {
        groupData[item] = null;
      }
    });

    groupData["transactionAdditionalData"] = transactionAdditionalDataList;
    groupData["purchasedItemAdditionalData"] = purchasedItemAdditionalDataList;

    return groupData;
  })
}

const cleanEarningRuleCreationData = (earningRule) => {
  if (!earningRule?.type) {
    return {}
  }
  const key = EarningRuleTypeKey[earningRule.type];
  let baseData = {
    name: earningRule.translations?.en?.generalName,
    type: earningRule.type,
    instructionSectionTitle:
      earningRule.translations?.en?.instructionSectionTitle,
    instructionSectionContent:
      earningRule.translations?.en?.instructionSectionContent,
    detailSectionTitle:
      earningRule.translations?.en?.detailSectionTitle,
    detailSectionContent:
      earningRule.translations?.en?.detailSectionContent,
    translations: postTranslations(earningRule.translations),
    linkedCampaignType: earningRule.linkedStampCampaign
      ? EarningRuleLinkedCampaign.stampCampaign
      : EarningRuleLinkedCampaign.earningCampaign,
    triggerPriority: earningRule?.executionPriority,
    [`${key}TypeRewardType`]: earningRule.rewardType,
  };
  if (earningRule?.pk) {
    baseData['id'] = earningRule.pk;
  }
  // rewards
  let rewardData = {};
  if (earningRule.rewardType === EarningRuleRewardType.points) {
    rewardData[`${key}TypePointsRewardTypePoints`] =
      earningRule.quantity;
    rewardData[`${key}TypePointsRewardTypeTpe`] =
      earningRule.tpeQuantity || null;
  } else if (
    earningRule.rewardType === EarningRuleRewardType.stamps
  ) {
    rewardData[`${key}TypeStampRewardTypeQuantity`] =
      earningRule.quantity;
  } else {
    rewardData[`${key}TypeCouponRewardTypeCouponTemplate`] =
      earningRule.coupons.pk;
    rewardData[`${key}TypeCouponRewardTypeQuantity`] =
      earningRule.quantity;
  }

  const recurringDateInfo = earningRule?.recurringDate
  if (recurringDateInfo?.pk) {
    recurringDateInfo.id = recurringDateInfo.pk
    delete recurringDateInfo.pk;
  }

  let values = {};

  const getTargetedSegments = (tempEarningRule) => {
    if (!tempEarningRule?.isExclusive) {
      return [];
    }
    if (
      tempEarningRule?.targetCustomerType === TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT
    ) {
      return (
        tempEarningRule?.dynamicSegments?.map((item) => item.pk) || []
      );
    }
    if (
      tempEarningRule?.targetCustomerType === TARGET_CUSTOMER_TYPE.STATIC_SEGMENT
    ) {
      return (
        tempEarningRule?.staticSegments?.map((item) => item.pk) || []
      );
    }
    return [];
  };
  const getTargetedLevels = (tempEarningRule) => {
    if (
      tempEarningRule?.isExclusive &&
      tempEarningRule?.targetCustomerType === TARGET_CUSTOMER_TYPE.LEVEL
    ) {
      return (
        tempEarningRule?.targetedLevels?.map((item) => item.pk) || []
      );
    }
    return [];
  };

  const blacklistSegmentsData = (tempEarningRule) => {
    if (earningRule.linkedStampCampaign || !tempEarningRule?.excludeBlacklistSegment) {
      return {
        excludeBlacklistSegment: false,
        targetedBlacklistSegments: []
      }
    }
    return {
      excludeBlacklistSegment: true,
      targetedBlacklistSegments: tempEarningRule?.blacklistSegments?.map((item) => item.pk) || [],
    }
  }

  const targetCustomerFieldsData = (tempEarningRule) => {
    return {
      isExclusive: tempEarningRule?.isExclusive,
      targetType: tempEarningRule?.isExclusive
        ? tempEarningRule?.targetCustomerType
        : TARGET_CUSTOMER_TYPE.DYNAMIC_SEGMENT,
      targetedSegments: getTargetedSegments(tempEarningRule),
      targetedLevels: getTargetedLevels(tempEarningRule),
      ...blacklistSegmentsData(tempEarningRule)
    }
  }

  switch (earningRule.type) {
    case EarningRuleType.newMember:
      values = {
        newMemberTypeRequirePromotionCode: earningRule?.requirePromotionCode || false,
        newMemberTypePromotionCodeUsageMaxLimit: earningRule?.requirePromotionCode 
        ? earningRule?.promotionCodeUsageMaxLimit || null
        : null,
        newMemberTypePromotionCode: earningRule?.requirePromotionCode
          ? earningRule?.promotionCode
          : null,
        startDate: earningRule?.isAlwaysActivePeriod
          ? earningRule?.creationDate || new Date()
          : earningRule?.activeStartDate,
        endDate: !earningRule?.isAlwaysActivePeriod
          ? earningRule?.activeEndDate
          : null,
        ...targetCustomerFieldsData(earningRule),
        ...rewardData,
      };
      break;
    case EarningRuleType.generalPurchase:
      const specialSkusFormated =
        earningRule.specialSkus?.map((item) => ({
          skuId: item.pk,
          sku: item.name,
          categoryName: null,
          categoryId: null,
        })) || [];
      const inputSkusFormated =
        earningRule.inputSkus?.filter(Boolean).map((item) => ({
          skuId: item,
          sku: item,
        })) || [];
      let generalPurchaseTypeLimitedSkus;
      let generalPurchaseTypeSkuFileImportTime;

      if (earningRule?.lineItemRequirement) {
        generalPurchaseTypeLimitedSkus = specialSkusFormated.concat(inputSkusFormated);
        generalPurchaseTypeSkuFileImportTime = earningRule?.skuFileImportTime;
      }
      values = {
        generalPurchaseTypeMinimumSpending: returnDefaultValueWhileEmpty(earningRule.minSpending, null),
        generalPurchaseTypeMinimumQuantity: returnDefaultValueWhileEmpty(earningRule.minQuantity, null),
        generalPurchaseTypeEligibleNumberOfDaysSincePurchase:
          returnDefaultValueWhileEmpty(earningRule.eligibleDays, null),
        generalPurchaseTypeUploadTransactionEligibleDays:
          returnDefaultValueWhileEmpty(earningRule.uploadEligibleDays, null),
        generalPurchaseTypeWillExcludeDeliveryCost:
          earningRule.isExcludeDeliveryCost || false,
        generalPurchaseTypeWillExcludeOtherCharges:
          earningRule.isExcludeOtherCharges || false,
        generalPurchaseTypeOnlyForBirthMonth:
          earningRule.isInBirthMonth || false,
        generalPurchaseTypeLimitedToStores: earningRule.selectedStores
          ? earningRule.selectedStores.map((item, index) => item.pk)
          : [],
        generalPurchaseTypeRewardType: earningRule.rewardType,
        // generalPurchaseTypeLimitedSkus: tempEarningRule.specialSkus,
        generalPurchaseTypeLimitedSkus,
        generalPurchaseTypeSkuFileImportTime,
        startDate: earningRule?.isAlwaysActivePeriod
          ? earningRule?.creationDate || new Date()
          : earningRule?.activeStartDate,
        endDate: !earningRule?.isAlwaysActivePeriod
          ? earningRule?.activeEndDate
          : null,
        ...targetCustomerFieldsData(earningRule),
        generalPurchaseTypeLineItemRequirement: earningRule?.lineItemRequirement || false,
        generalPurchaseTypeUseAdvancedLineItemGroups: earningRule?.useAdvancedLineItemGroups || false,
        generalPurchaseTypeLineItemGroupsUseOrRelationship: earningRule?.lineITemGroupsUseOr || false,
        generalPurchaseTypeLineItemGroups: cleanLineItemGroups(earningRule?.lineItemGroups) || [],
        generalPurchaseTypeRewardLogicType: earningRule?.lineItemRequirement 
        ? earningRule?.rewardLogicType 
        : REWARD_LOGIC_TYPE.baseOnTransactionValue,
        generalPurchaseTypeRewardAmountLogicType: earningRule?.rewardAmountType || REWARD_AMOUNT_TYPE.oneOff,
        generalPurchaseTypeRewardAmountBasedType: earningRule?.lineItemRequirement
          && earningRule?.rewardAmountType === REWARD_AMOUNT_TYPE.dynamic
          ? earningRule?.rewardAmountBasedType : null,
        // earning rule reward limit
        generalPurchaseTypeOverallRewardLimit: JSON.stringify({
          COUPONS: returnDefaultValueWhileEmpty(earningRule?.earningRuleAllTimeLimitCoupons, null),
          POINTS: returnDefaultValueWhileEmpty(earningRule?.earningRuleAllTimeLimitPoints, null),
          PROGRESSION_POINTS: returnDefaultValueWhileEmpty(earningRule?.earningRuleAllTimeLimitPp, null),
          STAMPS: returnDefaultValueWhileEmpty(earningRule?.earningRuleAllTimeLimitStamps, null),
        }),
        generalPurchaseTypeOverallLimit: returnDefaultValueWhileEmpty(earningRule?.earningRuleAllTimeLimitMaxTriggerTimes, null),

        generalPurchaseTypePeriodicType: earningRule?.earningRuleTimePeriodLimitPeriodicType,
        generalPurchaseTypePeriodicLimitEffectiveNumberOfDays: earningRule.earningRuleTimePeriodLimitPeriodicTypePerDays || null,
        generalPurchaseTypePeriodicFollowActivePeriod: earningRule?.earningRuleTimePeriodLimitPeriodicFollowActive,
        generalPurchaseTypePeriodicStartTime: earningRule?.earningRuleTimePeriodLimitPeriodicStartTime,
        generalPurchaseTypePeriodicEndTime: earningRule?.earningRuleTimePeriodLimitPeriodicEndTime,
        generalPurchaseTypePeriodicRewardLimit: JSON.stringify({
          COUPONS: returnDefaultValueWhileEmpty(earningRule?.earningRuleTimePeriodLimitCoupons, null),
          POINTS: returnDefaultValueWhileEmpty(earningRule?.earningRuleTimePeriodLimitPoints, null),
          PROGRESSION_POINTS: returnDefaultValueWhileEmpty(earningRule?.earningRuleTimePeriodLimitPp, null),
          STAMPS: returnDefaultValueWhileEmpty(earningRule?.earningRuleTimePeriodLimitStamps, null),
        }),
        generalPurchaseTypePeriodicLimit: returnDefaultValueWhileEmpty(earningRule?.earningRuleTimePeriodLimitMaxTriggerTimes, null),
        // per member rewad limit
        generalPurchaseTypePerHeadRewardLimit: JSON.stringify({
          COUPONS: returnDefaultValueWhileEmpty(earningRule?.perMemberAllTimeLimitCoupons, null),
          POINTS: returnDefaultValueWhileEmpty(earningRule?.perMemberAllTimeLimitPoints, null),
          PROGRESSION_POINTS: returnDefaultValueWhileEmpty(earningRule?.perMemberAllTimeLimitPp, null),
          STAMPS: returnDefaultValueWhileEmpty(earningRule?.perMemberAllTimeLimitStamps, null),
        }),
        generalPurchaseTypePerHeadLimit: returnDefaultValueWhileEmpty(earningRule?.perMemberAllTimeLimitMaxTriggerTimes, null),

        generalPurchaseTypePerHeadPeriodicType: earningRule?.perMemberTimePeriodLimitPeriodicType,
        generalPurchaseTypePerHeadPeriodicLimitEffectiveNumberOfDays: returnDefaultValueWhileEmpty(earningRule?.perMemberTimePeriodLimitPeriodicTypePerDays, null),
        generalPurchaseTypePerHeadPeriodicFollowActivePeriod: earningRule?.perMemberTimePeriodLimitPeriodicFollowActivePeriod,
        generalPurchaseTypePerHeadPeriodicStartTime: earningRule?.perMemberTimePeriodLimitPeriodicStartTime,
        generalPurchaseTypePerHeadPeriodicEndTime: earningRule?.perMemberTimePeriodLimitPeriodicEndTime,

        generalPurchaseTypePerHeadPeriodicRewardLimit: JSON.stringify({
          COUPONS: returnDefaultValueWhileEmpty(earningRule?.perMemberTimePeriodLimitCoupons, null),
          POINTS: returnDefaultValueWhileEmpty(earningRule?.perMemberTimePeriodLimitPoints, null),
          PROGRESSION_POINTS: returnDefaultValueWhileEmpty(earningRule?.perMemberTimePeriodLimitPp, null),
          STAMPS: returnDefaultValueWhileEmpty(earningRule?.perMemberTimePeriodLimitStamps, null),
        }),
        generalPurchaseTypePerHeadPeriodicLimit: returnDefaultValueWhileEmpty(earningRule?.perMemberTimePeriodLimitMaxTriggerTimes, null),
        // per transaction rewad limit
        generalPurchaseTypePerTransactionRewardLimit: JSON.stringify({
          COUPONS: returnDefaultValueWhileEmpty(earningRule?.perTransactionLimitCoupons, null),
          POINTS: returnDefaultValueWhileEmpty(earningRule?.perTransactionLimitPoints, null),
          PROGRESSION_POINTS: returnDefaultValueWhileEmpty(earningRule?.perTransactionLimitPp, null),
          STAMPS: returnDefaultValueWhileEmpty(earningRule?.perTransactionLimitStamps, null),
        }),
        recurringDate: recurringDateInfo,
      };
      if (earningRule.rewardType === EarningRuleRewardType.coupons) {
        values['generalPurchaseTypeCouponRewardTypeCouponTemplate'] =
          earningRule.coupons.pk;
        values['generalPurchaseTypeCouponRewardTypeCouponPerXValue'] =
          returnDefaultValueWhileEmpty(earningRule?.couponTypeRewardPerXValue, null);
        values['generalPurchaseTypeCouponRewardTypeCouponX'] =
          returnDefaultValueWhileEmpty(earningRule?.couponTypeRewardPerX);
      } else if (
        earningRule.rewardType === EarningRuleRewardType.stamps
      ) {
        values['generalPurchaseTypeStampRewardTypeX'] =
          returnDefaultValueWhileEmpty(earningRule.stampTypeRewardPerX);
        values[
          'generalPurchaseTypeStampRewardTypeStampsPerXValue'
        ] = returnDefaultValueWhileEmpty(earningRule.stampTypeRewardPerXValue);
      } else {
        values[
          'generalPurchaseTypePointsRewardTypePointsPerXValue'
        ] = earningRule.pointTypeRewardPerXValue;
        values['generalPurchaseTypePointsRewardTypeX'] =
          returnDefaultValueWhileEmpty(earningRule.pointTypeRewardPerX);
        values['generalPurchaseTypePointsRewardTypeTpePerXValue'] =
          returnDefaultValueWhileEmpty(earningRule.tpeTypeRewardPerXValue);
        values['generalPurchaseTypePointsRewardTypeTpeX'] =
          returnDefaultValueWhileEmpty(earningRule.tpeTypeRewardPerX);
      }
      break;
    case EarningRuleType.gpsCheckIn:
      values = {
        gpsCheckInTypeLatitude: earningRule.latitude,
        gpsCheckInTypeLongitude: earningRule.longitude,
        gpsCheckInTypeRadiusInMeter: earningRule.radius,
        gpsCheckInTypeOverallLimit: earningRule.overallLimit || null,
        gpsCheckInTypePerHeadLimit: earningRule.perHeadLimit || null,
        gpsCheckInTypePeriodicLimit:
          earningRule.periodicLimit || null,
        gpsCheckInTypePeriodicLimitEffectiveNumberOfDays:
          earningRule.periodicLimitDays || null,
        gpsCheckInTypePerHeadPeriodicLimit:
          earningRule.perHeadPeriodicLimit || null,
        gpsCheckInTypePerHeadPeriodicLimitEffectiveNumberOfDays:
          earningRule.perHeadPeriodicLimitDays || null,
        ...rewardData,
      };
      break;
    case EarningRuleType.memberReferral:
      values = {
        memberReferralTypeBeneficiary:
          earningRule.referralType.toUpperCase(),
        memberReferralTypeLimit: earningRule.maxInvitees || null,
        ...rewardData,
      };
      break;
    case EarningRuleType.birthday:
      values = {
        startDate: earningRule?.isAlwaysActivePeriod
          ? earningRule?.creationDate || new Date()
          : earningRule?.activeStartDate,
        endDate: !earningRule?.isAlwaysActivePeriod
          ? earningRule?.activeEndDate
          : null,
        ...targetCustomerFieldsData(earningRule),
        ...rewardData,
        recurringDate: recurringDateInfo,
      };
      break;
    case EarningRuleType.qrCodeScanning:
      values = {
        qrCodeScanningTypeQrCodes: earningRule.qrCodes,
        qrCodeScanningTypeOverallLimit:
          earningRule.overallLimit || null,
        qrCodeScanningTypePerHeadLimit:
          earningRule.perHeadLimit || null,
        qrCodeScanningTypePeriodicLimit:
          earningRule.periodicLimit || null,
        qrCodeScanningTypePeriodicLimitEffectiveNumberOfDays:
          earningRule.periodicLimitDays || null,
        qrCodeScanningTypePerHeadPeriodicLimit:
          earningRule.perHeadPeriodicLimit || null,
        qrCodeScanningTypePerHeadPeriodicLimitEffectiveNumberOfDays:
          earningRule.perHeadPeriodicLimitDays || null,
        ...rewardData,
      };
      break;
    case EarningRuleType.fillingForm:
      values = {
        // fillingFormTypeForm: earningRule.fillingForm.pk,
        // fillingFormTypeDefinitionType: earningRule.definitionType,
        fillingFormTypeOverallLimit: earningRule.overallLimit || null,
        fillingFormTypePerHeadLimit: earningRule.perHeadLimit || null,
        fillingFormTypePeriodicLimit:
          earningRule.periodicLimit || null,
        fillingFormTypePeriodicLimitEffectiveNumberOfDays:
          earningRule.periodicLimitDays || null,
        fillingFormTypePerHeadPeriodicLimit:
          earningRule.perHeadPeriodicLimit || null,
        fillingFormTypePerHeadPeriodicLimitEffectiveNumberOfDays:
          earningRule.perHeadPeriodicLimitDays || null,
        ...targetCustomerFieldsData(earningRule),
        ...rewardData,
      };
      break;
    case EarningRuleType.levelUpgrade:
      values = {
        levelUpgradeTypeLevelGoal: earningRule.levelGoal.pk || null,
        levelUpgradeTypePerHeadPeriodicLimit:
          earningRule.perHeadPeriodicLimit || null,
        levelUpgradeTypePerHeadPeriodicLimitEffectiveNumberOfDays:
          earningRule.perHeadPeriodicLimitDays || null,
        startDate: earningRule?.isAlwaysActivePeriod
          ? earningRule?.creationDate || new Date()
          : earningRule?.activeStartDate,
        endDate: !earningRule?.isAlwaysActivePeriod
          ? earningRule?.activeEndDate
          : null,
        ...targetCustomerFieldsData(earningRule),
        ...rewardData,
      };
      break;
    default:
      values = {
        ...rewardData,
      };
      break;
  }

  if (earningRule?.linkedStampCampaign) {
    values['startDate'] = null;
    values['endDate'] = null;
    values['isExclusive'] = false;
    values['targetedCustomerGroups'] = [];
    values['targetedSegments'] = [];
    values['targetedLevels'] = [];
  }

  const blackoutPeriod = (earningRule.activePeriodValues ?? [])
    .filter((item) => {
      return !!item?.startDate && !!item?.endDate;
    })
    .map((item) => {
      const data = { startDate: item.startDate, endDate: item.endDate };
      return data;
    });
  const blackoutWeekday = earningRule.activeWeekday;

  return {
    ...baseData,
    ...values,
    blackoutPeriod,
    blackoutWeekday,
  }
}

export default {
  namespace: 'createEarningRules',
  state: getInitialState(),
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    stepChange(state, { payload }) {
      const isBack = payload.isBack;
      let step = payload.step;
      const isValid = payload.isValid;
      const stepConfig = getNewStepConfig(
        step,
        state.stepConfig,
        !isValid,
        isBack,
      );
      if (isValid) {
        step = isBack ? step - 1 : step + 1;
      }
      console.log('@@106: ', step, '->', isValid, payload);
      return {
        ...state,
        currentStep: step,
        stepConfig,
        createStatus: APIStatus.none,
      };
    },

    loadEarningRuleFromCookie(state, { payload }) {
      const earningRule =
        getObjectFromSessionStorage(EARNING_RULES_SESSION_KEY) || {};
      // const defaultData = payload ? payload.data.earningRule : {};

      // let earningRule = defaultData;
      // if (!tempEarningRule) {
      //   earningRule = parseEarningRule(defaultData)
      // }

      // if (!defaultData.pk) {
      //   earningRule = tempEarningRule || {};
      //   delete earningRule.id;
      //   delete earningRule.pk;
      // }
      console.log('@259 earningRule', earningRule);
      // saveToSessionStorage(EARNING_RULES_SESSION_KEY, earningRule);
      return {
        ...state,
        earningRule,
        hasUpdatedDefaultValues: true,
        skusHaveLoaded: true,
      };
    },
    removeEarningRuleFromCookie(state, { payload }) {
      removeFromSessionStorage(EARNING_RULES_SESSION_KEY);

      return {
        ...state,
        hasUpdatedDefaultValues: false,
      };
    },
    // clearData(state, { payload }) {
    //   return { ...state, ...getInitState() };
    // },

    // saveOrRemoveEarningRuleFromCookie(state, { payload }) {
    //   let campaign = getObjectFromSessionStorage(EARNING_RULES_SESSION_KEY);
    //   if (payload.data) {
    //     campaign = { ...payload.data };
    //     saveToSessionStorage(EARNING_RULES_SESSION_KEY, campaign);
    //   } else {
    //     removeFromSessionStorage(EARNING_RULES_SESSION_KEY);
    //   }
    //   return {
    //     ...state,
    //   };
    // },
    changeVals(state, { payload }) {
      console.log('@@138: vals changed', payload);
      let tempEarningRule = getObjectFromSessionStorage(
        EARNING_RULES_SESSION_KEY,
      );
      if (payload.vals) {
        let data = {};
        if (payload.language) {
          data[payload.language] = {
            ...tempEarningRule[payload.language],
            ...payload.vals,
          };
        } else {
          data = payload.vals;
        }

        tempEarningRule = { ...tempEarningRule, ...data };
        saveToSessionStorage(EARNING_RULES_SESSION_KEY, tempEarningRule);
      }

      return {
        ...state,
        formChanged: true,
      };
    },
    assembleEarningRuleProducts(state, { payload }) {
      const specialSkus = payload.list?.filter((item) => item.node.skuId !== item.node.sku)
        .map((item) => ({
          pk: item.node.skuId,
          name: item.node.sku,
          category: {
            pk: item.node.categoryId,
            name: item.node.categoryName,
          },
        }));
      const inputSkus = payload.list?.filter((item) => item.node.skuId === item.node.sku)
        .map((item) => item.node.sku)

      return {
        ...state,
        earningRule: {
          ...state.earningRule,
          specialSkus: payload.page > 1 ? [...state.earningRule.specialSkus, ...specialSkus] : specialSkus,
          inputSkus: payload.page > 1 ? [...state.earningRule.inputSkus, ...inputSkus] : inputSkus,
        }
      }
    },

    clearData(state, { payload }) {
      return { ...state, ...getInitialState() };
    },
  },

  effects: {
    getOneEarningRule: [
      function* ({ payload }, { call, select, put }) {
        yield put({
          type: 'updateState',
          payload: { createStatus: APIStatus.calling },
        });
        const serviceArgs = [
          getOneEarningRule,
          convertPKToId('EarningRuleNode', payload.id),
        ];
        function* onSuccess(data) {
          console.log(data);
          const earningRule = parseEarningRule(data?.earningRule);

          yield put({
            type: 'updateState',
            payload: {
              earningRule: earningRule,
              createStatus: APIStatus.none,
              hasUpdatedDefaultValues: true,
            },
          });
          const action = payload?.afterAction || (() => { });
          yield action(earningRule);
          yield put({
            type: 'getEarningRuleProducts',
            payload: { earningRuleId: payload.id }
          })
        }

        function* onFailed(data) {
          console.log('@@getOneEarningRule error: ', data);
          yield put({
            type: 'updateState',
            payload: {
              skusHaveLoaded: true,
            }
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
      { type: 'takeLatest' },
    ],
    getEarningRuleProducts: [
      function* ({ payload }, { call, all, put }) {
        const page = payload.page || 1;
        const pageCursor = page > 1 ? convertNumberToCursor((page - 1) * 100 - 1) : '';
        const serviceArgs = [
          getEarningRuleProducts,
          payload.earningRuleId,
          pageCursor,
        ];
        function* onSuccess(data) {
          const products = data.products;
          yield put({
            type: 'assembleEarningRuleProducts',
            payload: {
              page,
              list: products.edges,
            },
          });
          if (products.pageInfo?.hasNextPage) {
            yield put({
              type: 'getEarningRuleProducts',
              payload: {
                earningRuleId: payload.earningRuleId,
                page: page + 1,
              }
            })
          } else {
            yield put({
              type: 'updateState',
              payload: {
                skusHaveLoaded: true,
              }
            });
          }
        }
        // eslint-disable-next-line require-yield
        function* onFailed(data) {
          console.log('@@getEarningRuleProducts error: ', data);
          yield put({
            type: 'updateState',
            payload: {
              skusHaveLoaded: true,
            }
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed, onFailed);
      },
    ],
    createOrUpdateEarningRule: [
      function* ({ payload }, { call, all, put, select }) {
        // const tempEarningRule = getObjectFromSessionStorage(EARNING_RULES_SESSION_KEY);
        const tempEarningRule = payload.data;
        console.log('@327', tempEarningRule, payload);
        if (!tempEarningRule?.type) {
          return;
        }
        const cleanedData = cleanEarningRuleCreationData(tempEarningRule)

        const serviceArgs = [createOrUpdateEarningRule, cleanedData];
        console.log('@@493: ', cleanedData);
        const afterAction = payload.afterAction || (() => { });
        function* onSuccess(data) {
          console.log('@@267', data);
          if (
            ('createEarningRule' in data && data.createEarningRule.errors) ||
            ('updateEarningRule' in data && data.updateEarningRule.errors)
          ) {
            yield put({
              type: 'updateState',
              payload: {
                createStatus: APIStatus.failed,
                formChanged: false,
                checked: CheckStatus.initOrNotChecked,
              },
            });
          } else {
            yield put({
              type: 'updateState',
              payload: {
                createStatus: APIStatus.success,
                formChanged: false,
                earningRule: {
                  pk: data.createEarningRule?.node?.pk,
                  type: data.createEarningRule?.node?.type,
                },
                checked: CheckStatus.initOrNotChecked,
              },
            });
          }

          yield put({ type: 'removeEarningRuleFromCookie' });
          afterAction();
        }

        function* onFail(errors) {
          console.log('@275:', errors);

          yield put({
            type: 'updateState',
            payload: {
              createStatus: APIStatus.failed,
              formChanged: false,
              checked: CheckStatus.initOrNotChecked,
            },
          });
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFail);
      },
      { type: 'takeLatest' },
    ],
    getWebhookAuthenticatedKey: [
      function* ({ payload }, { put }) {
        const serviceArgs = [getWebhookAuthenticatedKey, payload.earningRuleId];
        function* onSuccess(data) {
          const webhookAuthenticatedKeyNode =
            data?.authenticatedKeys?.edges[0]?.node;
          yield put({
            type: 'updateState',
            payload: {
              webhookAuthenticatedKey: webhookAuthenticatedKeyNode,
              isCopied: webhookAuthenticatedKeyNode?.isCopied,
            },
          });
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    createOrUpdateWebhookAuthenticatedKey: [
      function* ({ payload }, { put }) {
        const afterAction = payload.afterAction || (() => { });
        const serviceArgs = [
          createOrUpdateWebhookAuthenticatedKey,
          { ...payload },
        ];
        function* onSuccess(data) {
          const action = payload?.id ? 'update' : 'create';
          const webhookAuthenticatedKeyNode =
            data?.[`${action}AuthenticatedKey`]?.node;
          yield put({
            type: 'updateState',
            payload: {
              webhookAuthenticatedKey: webhookAuthenticatedKeyNode,
              isCopied: webhookAuthenticatedKeyNode?.isCopied,
            },
          });
          afterAction(webhookAuthenticatedKeyNode);
        }

        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
  },
};
